import React from 'react'
import TextInput from '../inputs/TextInput'
import ColorPicker from '../ColorPicker'
import SelectInput from '../inputs/SelectInput'
import SpacingInput from '../inputs/SpacingInput'
import { updatePropertyWithClass } from '../../../utils/cssClassGenerator'
import { useEditorStore } from '../../../stores/editorStore'

interface ButtonPropertyFieldProps {
	value: any
	onChange: (value: any) => void
	placeholder?: string
	sectionName?: string
	propName?: string
	arrayIndex?: number
}

// Função para converter string de estilo em objeto
const parseStyleString = (styleString: any): Record<string, string> => {
	if (!styleString || typeof styleString !== 'string') return {}

	const styles: Record<string, string> = {}
	styleString.split(';').forEach((rule: string) => {
		const [property, value] = rule.split(':').map((s) => s.trim())
		if (property && value) {
			styles[property] = value
		}
	})
	return styles
}

// Função para converter objeto de estilo em string
const buildStyleString = (styles: Record<string, string>): string => {
	const styleEntries: string[] = []
	
	// Adiciona todas as propriedades (incluindo borda)
	Object.entries(styles).forEach(([property, value]) => {
		if (value !== undefined && value !== '' && !property.startsWith('--hover-')) {
			styleEntries.push(`${property}: ${value}`)
		}
	})
	
	// Adiciona estilos de hover se existirem
	if (styles['--hover-color']) {
		styleEntries.push(`--hover-color: ${styles['--hover-color']}`)
	}
	if (styles['--hover-background-color']) {
		styleEntries.push(`--hover-background-color: ${styles['--hover-background-color']}`)
	}
	if (styles['--hover-border-color']) {
		styleEntries.push(`--hover-border-color: ${styles['--hover-border-color']}`)
	}
	
	return styleEntries.join('; ')
}

// Função para mapear alinhamento para align-self
const mapAlignmentToAlignSelf = (alignment: string): string => {
	switch (alignment) {
		case 'left':
			return 'flex-start'
		case 'center':
			return 'center'
		case 'right':
			return 'flex-end'
		default:
			return 'center'
	}
}

// Função para mapear align-self para alinhamento
const mapAlignSelfToAlignment = (alignSelf: string): string => {
	switch (alignSelf) {
		case 'flex-start':
			return 'left'
		case 'center':
			return 'center'
		case 'flex-end':
			return 'right'
		default:
			return 'center'
	}
}

export default function ButtonPropertyField({ value, onChange, placeholder, sectionName, propName, arrayIndex }: ButtonPropertyFieldProps) {
	const { previewMode } = useEditorStore()
	// Estrutura: { text: { value }, link: { value }, style: {desktop, mobile} }
	const textValue = value?.text?.value || ''
	const linkValue = value?.link?.value || ''
	const rawStyle = value?.style
	
	// Garantir que style seja um objeto com desktop e mobile
	const styleObj = typeof rawStyle === 'string' 
		? { desktop: rawStyle, mobile: '' }
		: { desktop: rawStyle?.desktop || '', mobile: rawStyle?.mobile || '' }
	
	const currentStyle = styleObj[previewMode] || ''
	const styleValue = parseStyleString(currentStyle)
	
	// Extrair alinhamento do align-self atual
	const currentAlignSelf = styleValue['align-self'] || 'center'
	const alignmentValue = mapAlignSelfToAlignment(currentAlignSelf)

	const [text, setText] = React.useState(textValue)
	const [link, setLink] = React.useState(linkValue)
	const [alignment, setAlignment] = React.useState(alignmentValue)
	const [localStyleObj, setLocalStyleObj] = React.useState(styleValue)

	React.useEffect(() => {
		const txt = value?.text?.value || ''
		const lnk = value?.link?.value || ''
		const rs = value?.style
		
		// Garantir que style seja um objeto com desktop e mobile
		const styleObj = typeof rs === 'string' 
			? { desktop: rs, mobile: '' }
			: { desktop: rs?.desktop || '', mobile: rs?.mobile || '' }
		
		const currentStyle = styleObj[previewMode] || ''
		const styleValue = parseStyleString(currentStyle)
		
		// Extrair alinhamento do align-self atual
		const currentAlignSelf = styleValue['align-self'] || 'center'
		const alignValue = mapAlignSelfToAlignment(currentAlignSelf)
		
		setText(txt)
		setLink(lnk)
		setAlignment(alignValue)
		setLocalStyleObj(styleValue)
	}, [value, previewMode])

	const emit = (next: { text: string; link: string; style: Record<string,string>; alignment: string }) => {
		// Atualizar align-self baseado no alinhamento selecionado
		const newStyleObj = { ...next.style }
		const alignSelfValue = mapAlignmentToAlignSelf(next.alignment)
		newStyleObj['align-self'] = alignSelfValue
		
		const styleString = buildStyleString(newStyleObj)
		
		// Manter a estrutura de style como objeto com desktop e mobile
		const currentStyleObj = typeof value?.style === 'string' 
			? { desktop: value.style, mobile: '' }
			: { desktop: value?.style?.desktop || '', mobile: value?.style?.mobile || '' }
		
		const newStyleStructure = {
			...currentStyleObj,
			[previewMode]: styleString
		}
		
		const newButtonValue = {
			text: { value: next.text },
			link: { value: next.link },
			style: newStyleStructure
		}
		
		if (sectionName && propName) {
			const updatedValue = updatePropertyWithClass(sectionName, propName, value, newButtonValue, arrayIndex)
			onChange(updatedValue)
		} else {
			onChange(newButtonValue)
		}
	}

	const handleValueChange = (newValue: string) => {
		setText(newValue)
		emit({ text: newValue, link, style: localStyleObj, alignment })
	}

	const handleLinkChange = (newLink: string) => {
		setLink(newLink)
		emit({ text, link: newLink, style: localStyleObj, alignment })
	}

	const handleStyleChangeLocal = (styleProperty: string, styleValue: string) => {
		const newStyleObj = { ...localStyleObj, [styleProperty]: styleValue }
		setLocalStyleObj(newStyleObj)
		emit({ text, link, style: newStyleObj, alignment })
	}

	const handleAlignmentChange = (newAlignment: string) => {
		setAlignment(newAlignment)
		emit({ text, link, style: localStyleObj, alignment: newAlignment })
	}

	const fontWeightOptions = [
		{ value: '', label: 'Selecionar peso...' },
		{ value: 'normal', label: 'Normal' },
		{ value: 'bold', label: 'Negrito' },
		{ value: '600', label: 'Semi-negrito' },
		{ value: '700', label: 'Bold' },
		{ value: '800', label: 'Extra-bold' }
	]

	return (
		<div className="space-y-4">
			{/* Conteúdo do Botão */}
			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">Conteúdo</label>
				<TextInput 
					value={text} 
					onChange={handleValueChange} 
					placeholder="Texto do botão" 
				/>
			</div>

			{/* Link do Botão */}
			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">Link</label>
				<TextInput 
					type="url" 
					value={link} 
					onChange={handleLinkChange} 
					placeholder="https://..." 
				/>
			</div>

			<div className="space-y-3">
				<h4 className="text-sm font-medium text-gray-700">Estilos</h4>

				{/* Tamanho da Fonte */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Tamanho da Fonte</label>
					<SelectInput
						value={localStyleObj['font-size'] || ''}
						onChange={(v) => handleStyleChangeLocal('font-size', v)}
						options={[
							{ value: '', label: 'Selecionar tamanho...' },
							{ value: '12px', label: 'Pequeno (12px)' },
							{ value: '14px', label: 'Normal (14px)' },
							{ value: '16px', label: 'Médio (16px)' },
							{ value: '18px', label: 'Grande (18px)' },
							{ value: '20px', label: 'Extra Grande (20px)' },
							{ value: '24px', label: 'Título (24px)' },
							{ value: '32px', label: 'Título Grande (32px)' }
						]}
					/>
				</div>

				{/* Peso da Fonte */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Peso da Fonte</label>
					<SelectInput
						value={localStyleObj['font-weight'] || ''}
						onChange={(v) => handleStyleChangeLocal('font-weight', v)}
						options={fontWeightOptions}
					/>
				</div>

				{/* Cor do Texto */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor do Texto</label>
					<ColorPicker 
						color={localStyleObj['color'] || ''} 
						onChange={(color) => handleStyleChangeLocal('color', color)} 
					/>
				</div>

				{/* Cor de Fundo */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor de Fundo</label>
					<ColorPicker 
						color={localStyleObj['background-color'] || ''} 
						onChange={(color) => handleStyleChangeLocal('background-color', color)} 
					/>
				</div>

				{/* Tamanho da Borda */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Tamanho da Borda</label>
					<SelectInput
						value={localStyleObj['border-width'] || ''}
						onChange={(v) => {
							handleStyleChangeLocal('border-width', v)
						}}
						options={[
							{ value: '', label: 'Sem borda' },
							{ value: '1px', label: 'Fina (1px)' },
							{ value: '2px', label: 'Média (2px)' },
							{ value: '3px', label: 'Grossa (3px)' },
							{ value: '4px', label: 'Muito grossa (4px)' },
							{ value: '5px', label: 'Extra grossa (5px)' }
						]}
					/>
				</div>

				{/* Border Radius */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Border Radius</label>
					<SelectInput
						value={localStyleObj['border-radius'] || ''}
						onChange={(v) => {
							handleStyleChangeLocal('border-radius', v)
						}}
						options={[
							{ value: '', label: 'Sem arredondamento' },
							{ value: '2px', label: 'Leve (2px)' },
							{ value: '4px', label: 'Suave (4px)' },
							{ value: '8px', label: 'Médio (8px)' },
							{ value: '12px', label: 'Arredondado (12px)' },
							{ value: '20px', label: 'Muito arredondado (20px)' },
							{ value: '50%', label: 'Circular (50%)' }
						]}
					/>
				</div>

				{/* Cor da Borda */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor da Borda</label>
					<ColorPicker 
						color={localStyleObj['border-color'] || ''} 
						onChange={(color) => {
							handleStyleChangeLocal('border-color', color)
						}} 
					/>
				</div>

				{/* Estilo da Borda */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Estilo da Borda</label>
					<SelectInput
						value={localStyleObj['border-style'] || ''}
						onChange={(v) => {
							handleStyleChangeLocal('border-style', v)
						}}
						options={[
							{ value: '', label: 'Sólida' },
							{ value: 'solid', label: 'Sólida' },
							{ value: 'dashed', label: 'Tracejada' },
							{ value: 'dotted', label: 'Pontilhada' },
							{ value: 'double', label: 'Dupla' },
							{ value: 'groove', label: 'Sulcada' },
							{ value: 'ridge', label: 'Crista' },
							{ value: 'inset', label: 'Inserida' },
							{ value: 'outset', label: 'Projetada' }
						]}
					/>
				</div>

				{/* Separador para Hover */}
				<div className="border-t pt-4">
					<h4 className="text-sm font-medium text-gray-700 mb-3">Estados Hover</h4>
				</div>

				{/* Cor do Texto no Hover */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor do Texto (Hover)</label>
					<ColorPicker 
						color={localStyleObj['--hover-color'] || ''} 
						onChange={(color) => handleStyleChangeLocal('--hover-color', color)} 
					/>
				</div>

				{/* Cor de Fundo no Hover */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor de Fundo (Hover)</label>
					<ColorPicker 
						color={localStyleObj['--hover-background-color'] || ''} 
						onChange={(color) => handleStyleChangeLocal('--hover-background-color', color)} 
					/>
				</div>

				{/* Cor da Borda no Hover */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Cor da Borda (Hover)</label>
					<ColorPicker 
						color={localStyleObj['--hover-border-color'] || ''} 
						onChange={(color) => handleStyleChangeLocal('--hover-border-color', color)} 
					/>
				</div>

				{/* Alinhamento */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Alinhamento</label>
					<div className="flex space-x-1">
						{[
							{ value: 'left', icon: '←', label: 'Esquerda' },
							{ value: 'center', icon: '↔', label: 'Centro' },
							{ value: 'right', icon: '→', label: 'Direita' }
						].map((option) => {
							const isActive = alignment === option.value
							
							return (
								<button
									key={option.value}
									type="button"
									onClick={() => handleAlignmentChange(option.value)}
									className={`
										flex-1 px-3 py-2 text-xs border rounded-md transition-colors
										${isActive 
											? 'bg-gradient-primary text-white border-primary-500' 
											: 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
										}
									`}
									title={option.label}
								>
									<span className="text-sm font-bold">{option.icon}</span>
								</button>
							)
						})}
					</div>
				</div>

				{/* Padding */}
				<SpacingInput
					label="Padding"
					values={{
						top: localStyleObj['padding-top'] || '',
						right: localStyleObj['padding-right'] || '',
						bottom: localStyleObj['padding-bottom'] || '',
						left: localStyleObj['padding-left'] || ''
					}}
					onChange={(values) => {
						// Criar novo objeto de estilo
						const newStyleObj = { ...localStyleObj }
						
						// Primeiro, remove todas as propriedades de padding existentes
						delete newStyleObj['padding-top']
						delete newStyleObj['padding-right']
						delete newStyleObj['padding-bottom']
						delete newStyleObj['padding-left']
						
						// Depois, adiciona apenas as propriedades que foram enviadas
						Object.entries(values).forEach(([side, value]) => {
							const property = `padding-${side}`
							if (value !== undefined && value !== '') {
								newStyleObj[property] = value as string
							}
						})
						
						// Atualizar estado e chamar onChange
						setLocalStyleObj(newStyleObj)
						emit({ text, link, style: newStyleObj, alignment })
					}}
					unit="px"
				/>

				{/* Margin */}
				<SpacingInput
					label="Margem"
					values={{
						top: localStyleObj['margin-top'] || '',
						right: localStyleObj['margin-right'] || '',
						bottom: localStyleObj['margin-bottom'] || '',
						left: localStyleObj['margin-left'] || ''
					}}
					onChange={(values) => {
						// Criar novo objeto de estilo
						const newStyleObj = { ...localStyleObj }
						
						// Primeiro, remove todas as propriedades de margin existentes
						delete newStyleObj['margin-top']
						delete newStyleObj['margin-right']
						delete newStyleObj['margin-bottom']
						delete newStyleObj['margin-left']
						
						// Depois, adiciona apenas as propriedades que foram enviadas
						Object.entries(values).forEach(([side, value]) => {
							const property = `margin-${side}`
							if (value !== undefined && value !== '') {
								newStyleObj[property] = value as string
							}
						})
						
						// Atualizar estado e chamar onChange
						setLocalStyleObj(newStyleObj)
						emit({ text, link, style: newStyleObj, alignment })
					}}
					unit="px"
				/>
			</div>
		</div>
	)
}
